import { Clock3 } from 'lucide-react'
import Lang from '../../../config/lang'
import { formatLocalizedDate } from '../applicationMapper'
import ApplicationMetaSummary from './ApplicationMetaSummary'
import ApplicationNotice from './ApplicationNotice'
import ApplicationProgressTimeline from './ApplicationProgressTimeline'

const toDateKey = (value) => {
  const parsedDate = new Date(value)

  if (Number.isNaN(parsedDate.getTime())) return ''

  return `${parsedDate.getFullYear()}-${String(
    parsedDate.getMonth() + 1,
  ).padStart(2, '0')}-${String(parsedDate.getDate()).padStart(2, '0')}`
}

const normalizeHolidayDateSet = (holidayDates = []) =>
  new Set(
    holidayDates
      .map((item) => toDateKey(item))
      .filter((item) => String(item).trim() !== ''),
  )

const normalizeSlotsByStartTime = (slots = []) => {
  const startTimeSet = new Set()

  return slots.filter((slot) => {
    const startTime = String(slot?.start || '').trim()

    if (startTime === '' || startTimeSet.has(startTime)) return false

    startTimeSet.add(startTime)
    return true
  })
}

const isWeekendDate = (value) => {
  const parsedDate = new Date(value)

  if (Number.isNaN(parsedDate.getTime())) return false

  const day = parsedDate.getDay()
  return day === 0 || day === 6
}

const ScheduledNotice = ({ application, lang }) => {
  const scheduledAt = application?.interview?.selectedDateTime

  if (!scheduledAt) return null

  const dateLabel = formatLocalizedDate(scheduledAt, lang, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
  const timeLabel = formatLocalizedDate(scheduledAt, lang, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })

  return (
    <ApplicationNotice
      icon={<Clock3 className='h-[10px] w-[10px]' strokeWidth={1.8} />}
      tone='info'
    >
      <p>
        {Lang(lang, 'interview_selected_notice_before')}{' '}
        <span className='font-medium'>{dateLabel}</span>{' '}
        {Lang(lang, 'interview_selected_notice_at')}{' '}
        <span className='font-medium'>{timeLabel}</span>{' '}
        {Lang(lang, 'interview_selected_notice_after')}
      </p>
    </ApplicationNotice>
  )
}

const InterviewScheduleCard = ({
  application,
  holidayDates = [],
  lang,
  selectedDate,
  selectedSlotId,
  isSubmitting,
  isRescheduling,
  onDateChange,
  onSlotChange,
  onConfirm,
  onReschedule,
}) => {
  const now = new Date()
  const interviewStage =
    application?.interview?.stage ||
    application?.bookingMeta?.interviewStage ||
    application?.interviewStage ||
    'HC'
  const holidayDateSet = normalizeHolidayDateSet(holidayDates)
  const displaySchedule = (application?.interview?.schedule || [])
    .filter((day) => !isWeekendDate(day?.date))
    .map((day) => {
      const dateKey = toDateKey(day?.date)
      const slotsForDisplay = (
        Array.isArray(day?.slots) ? day.slots : []
      ).filter((slot) => {
        if (slot?.isFull) return false

        const slotStartDateTime = new Date(slot?.startDateTime)

        if (Number.isNaN(slotStartDateTime.getTime())) return false

        return slotStartDateTime.getTime() > now.getTime()
      })
      const hasAvailableSlots = slotsForDisplay.length > 0

      return {
        ...day,
        slots: slotsForDisplay,
        isDisabled:
          dateKey === '' ||
          !hasAvailableSlots ||
          holidayDateSet.has(dateKey) ||
          isWeekendDate(day?.date),
      }
    },
  )
  const stageLabelKey =
    interviewStage === 'USER' ? 'interview_user' : 'interview_hc'
  const scheduleLabelKey =
    interviewStage === 'USER'
      ? 'interview_user_schedule'
      : 'interview_hc_schedule'
  const selectedDay = displaySchedule.find((day) => day.date === selectedDate)
  const selectedSlot = selectedDay?.slots?.find(
    (slot) => String(slot.id) === String(selectedSlotId),
  )
  const selectedInterviewMeta = selectedSlot || application?.interview || {}
  const visibleSlots = selectedDay?.isDisabled ? [] : selectedDay?.slots || []
  const displaySlots = normalizeSlotsByStartTime(visibleSlots)
  const monthSource =
    selectedDay?.date ||
    displaySchedule.find((day) => !day.isDisabled)?.date ||
    displaySchedule[0]?.date
  const progressSteps =
    application?.progressSteps || application?.overviewMeta?.progressSteps || []
  const isScheduled = Boolean(
    application?.interview?.isScheduled ||
    application?.interview?.selectedDateTime,
  )
  const isRescheduleState =
    application?.bookingMeta?.isReschedule === true && !isScheduled
  const deadlineDate = application?.interview?.deadlineDate || ''

  const monthLabel = monthSource
    ? formatLocalizedDate(monthSource, lang, {
        month: 'long',
        year: 'numeric',
      })
    : '-'

  if (isScheduled) {
    return (
      <div className='rounded-[12px] border border-[#E5E7EB] bg-white p-4 shadow-[0px_1px_2px_rgba(0,0,0,0.05)] sm:p-5 md:p-[25px]'>
        <div className='flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6'>
          <div className='min-w-0'>
            <h3 className='break-words text-[20px] font-medium leading-[28px] text-[#111827]'>
              {application.title}
            </h3>
            <div className='mt-3 flex flex-wrap items-center'>
              <img
                src='/asset/icon/company.svg'
                alt='company'
                className='h-4 w-4'
              />
              <p className='ml-2 break-words text-[16px] font-normal leading-5 text-[#4B5563] uppercase'>
                {application?.company != null &&
                String(application.company).trim() !== ''
                  ? application.company
                  : '-'}
              </p>
            </div>
          </div>

          <span className='inline-flex w-fit self-start rounded-full border border-[#BFDBFE] bg-[#DBEAFE] px-[13px] py-[5px] text-[12px] font-medium leading-4 text-[#1D4ED8]'>
            {Lang(lang, scheduleLabelKey)}
          </span>
        </div>

        <div className='mt-8 flex flex-col gap-8'>
          <ScheduledNotice application={application} lang={lang} />
          <ApplicationMetaSummary
            variant='interview'
            lang={lang}
            interviewMethod={application?.interview?.method}
            interviewMethodNote={application?.interview?.methodNote}
            interviewLocation={
              application?.interview?.location || application?.location
            }
            interviewLink={application?.interview?.link}
            mapUrl={application?.interview?.mapUrl}
            linkUrl={application?.interview?.linkUrl}
          />
          {progressSteps.length > 0 ? (
            <ApplicationProgressTimeline
              items={progressSteps}
              lang={lang}
              showBottomBorder={false}
            />
          ) : null}
        </div>
      </div>
    )
  }

  if (isRescheduleState) {
    return (
      <div className='rounded-[12px] border border-[#E5E7EB] bg-white p-4 shadow-[0px_1px_2px_rgba(0,0,0,0.05)] sm:p-5 md:p-[25px]'>
        <div className='flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6'>
          <div className='min-w-0'>
            <h3 className='break-words text-[20px] font-medium leading-[28px] text-[#111827]'>
              {application.title}
            </h3>
            <div className='mt-3 flex flex-wrap items-center'>
              <img
                src='/asset/icon/company.svg'
                alt='company'
                className='h-4 w-4'
              />
              <p className='ml-2 break-words text-[16px] font-normal leading-5 text-[#4B5563] uppercase'>
                {application?.company != null &&
                String(application.company).trim() !== ''
                  ? application.company
                  : '-'}
              </p>
            </div>
          </div>

          <span className='inline-flex w-fit self-start rounded-full border border-[#BFDBFE] bg-[#DBEAFE] px-[13px] py-[5px] text-[12px] font-medium leading-4 text-[#1D4ED8]'>
            {Lang(lang, 'reschedule')}
          </span>
        </div>

        <ApplicationNotice
          icon={<Clock3 className='h-[10px] w-[10px]' strokeWidth={1.8} />}
          tone='info'
          className='mt-8'
        >
          <p>{Lang(lang, 'interview_reschedule_waiting_desc')}</p>
        </ApplicationNotice>

        <div className='mt-8'>
          <ApplicationMetaSummary
            dateApplied={
              application?.dateApplied ||
              application?.overviewMeta?.dateApplied ||
              ''
            }
            location={
              application?.location ||
              application?.overviewMeta?.location ||
              '-'
            }
            lang={lang}
          />
        </div>

        {progressSteps.length > 0 ? (
          <div className='mt-8'>
            <ApplicationProgressTimeline
              items={progressSteps}
              lang={lang}
              showBottomBorder={false}
            />
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div className='rounded-[12px] border border-[#E5E7EB] bg-white p-4 shadow-[0px_1px_2px_rgba(0,0,0,0.05)] sm:p-5 md:p-[25px]'>
      <div className='flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6'>
        <div className='min-w-0'>
          <h3 className='break-words text-[20px] font-medium leading-[28px] text-[#111827]'>
            {application.title}
          </h3>
          <div className='mt-3 flex flex-wrap items-center'>
            <img
              src='/asset/icon/company.svg'
              alt='company'
              className='h-4 w-4'
            />
            <p className='ml-2 break-words text-[16px] font-normal leading-5 text-[#4B5563] uppercase'>
              {application?.company != null &&
              String(application.company).trim() !== ''
                ? application.company
                : '-'}
            </p>
          </div>
        </div>

        <span className='inline-flex w-fit self-start rounded-full border border-[#BFDBFE] bg-[#DBEAFE] px-[13px] py-[5px] text-[12px] font-medium leading-4 text-[#1D4ED8]'>
          {application?.bookingMeta?.isReschedule
            ? Lang(lang, 'reschedule')
            : Lang(lang, stageLabelKey)}
        </span>
      </div>

      <ApplicationNotice
        variant='countdown'
        className='mt-8'
        countdownLabel={Lang(lang, 'interview_booking_expires_in')}
        countdownTargetDate={deadlineDate}
        countdownExpiryPrefix={Lang(lang, 'expires')}
        countdownExpiryText={formatLocalizedDate(deadlineDate, lang, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })}
      />

        <div className='mt-8'>
          <ApplicationMetaSummary
            variant='interview'
            lang={lang}
            interviewMethod={selectedInterviewMeta?.method}
            interviewMethodNote={selectedInterviewMeta?.methodNote}
            interviewLocation={selectedInterviewMeta?.location || application?.location}
            interviewLink={selectedInterviewMeta?.link}
            mapUrl={selectedInterviewMeta?.mapUrl}
            linkUrl={selectedInterviewMeta?.linkUrl}
          />
        </div>

      {progressSteps.length > 0 ? (
        <div className='mt-8'>
          <ApplicationProgressTimeline
            items={progressSteps}
            lang={lang}
            showBottomBorder
          />
        </div>
      ) : null}

      <div className='mt-8 rounded-[12px] border border-[#E2E8F0] bg-white p-4 md:p-5'>
        <div className='flex items-center gap-2 text-[#111827]'>
          <p className='text-[14px] font-medium leading-4 text-[#020817]'>
            {monthLabel}
          </p>
        </div>

        <div className='-mx-1 mt-4 flex gap-3 overflow-x-auto px-1 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'>
          {displaySchedule.map((day) => {
            const isSelected = !day.isDisabled && day.date === selectedDate

            return (
              <button
                key={day.date}
                type='button'
                disabled={day.isDisabled}
                onClick={() => {
                  if (!day.isDisabled) onDateChange(day.date)
                }}
                className={`flex min-w-[124px] flex-col items-center justify-center rounded-[8px] px-4 py-[8px] text-center transition sm:min-w-[142px] sm:px-[24px] ${
                  day.isDisabled
                    ? 'cursor-not-allowed bg-[#F8FAFC] opacity-60'
                    : isSelected
                      ? 'bg-[#FEF2F2]'
                      : 'bg-white hover:bg-[#FFF1F2]'
                }`}
              >
                <p
                  className={`text-[9.5px] font-normal leading-[15px] ${
                    day.isDisabled ? 'text-[#94A3B8]' : 'text-[#64748B]'
                  }`}
                >
                  {formatLocalizedDate(day.date, lang, { weekday: 'short' })}
                </p>

                <p
                  className={`text-[14px] font-medium leading-5 ${
                    day.isDisabled ? 'text-[#94A3B8]' : 'text-[#EA2427]'
                  }`}
                >
                  {formatLocalizedDate(day.date, lang, { day: 'numeric' })}
                </p>

                <span
                  className={`mt-[6px] block h-[4px] w-[4px] rounded-full ${
                    day.isDisabled
                      ? 'bg-[#CBD5E1]'
                      : isSelected
                        ? 'bg-[#EA2427]'
                        : 'bg-[#CBD5E1]'
                  }`}
                />
              </button>
            )
          })}
        </div>

        <div className='mt-2 border-t border-[#E5E7EB] pt-4'>
          <div className='flex flex-col gap-[2px]'>
            <p className='text-[12px] font-medium leading-4 text-[#020817]'>
              {Lang(lang, 'select_interview_time')}:
            </p>
            <p className='text-[12px] font-normal leading-4 text-[#64748B]'>
              {Lang(lang, 'helper_select_interview_time')}
            </p>
          </div>

          {displaySlots.length > 0 ? (
            <div className='mt-4 flex flex-wrap gap-3'>
              {displaySlots.map((slot) => {
                const isSelected = selectedSlot?.start === slot.start

                return (
                  <button
                    key={slot.id}
                    type='button'
                    disabled={slot.isFull}
                    onClick={() => onSlotChange(slot.id)}
                    className={`flex min-h-[58px] min-w-[84px] flex-col items-center justify-center rounded-[8px] border px-3 py-2 transition sm:min-w-[88px] ${
                      isSelected
                        ? 'border-[#EA2427] bg-[#EA2427]'
                        : 'border-[#E2E8F0] bg-white hover:border-[#FCA5A5]'
                    } ${slot.isFull ? 'cursor-not-allowed opacity-50' : ''}`}
                  >
                    <span
                      className={`leading-5 ${
                        isSelected
                          ? 'text-[13px] font-medium text-[#F8FAFC]'
                          : 'text-[13px] font-normal text-[#020817]'
                      }`}
                    >
                      {slot.start}
                    </span>
                  </button>
                )
              })}
            </div>
          ) : (
            <ApplicationNotice
              icon={<Clock3 className='h-[10px] w-[10px]' strokeWidth={1.8} />}
              tone='neutral'
              className='mt-4'
            >
              <p>{Lang(lang, 'no_interview_slots_available')}</p>
            </ApplicationNotice>
          )}
        </div>

        <div className='mt-5 flex flex-col gap-4 border-t border-[#E5E7EB] pt-4 md:flex-row md:items-center md:justify-between'>
          <p className='min-w-0 text-[13px] font-normal leading-5 text-[#020817] md:flex-1 md:whitespace-nowrap'>
            {Lang(lang, 'selected_schedule')}:{' '}
            {selectedDay && selectedSlot ? (
              <span className='font-medium text-[#020817]'>
                {formatLocalizedDate(selectedDay.date, lang, {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}{' '}
                - {selectedSlot.start} - {selectedSlot.end}
              </span>
            ) : (
              '-'
            )}
          </p>

          <div className='flex w-full flex-col gap-3 sm:w-auto sm:flex-row md:shrink-0'>
            <button
              type='button'
              onClick={onReschedule}
              disabled={isRescheduling}
              className='w-full rounded-full border border-[#D1D5DB] px-6 py-3 text-sm font-semibold text-[#374151] transition hover:border-[#9CA3AF] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto'
            >
              {isRescheduling
                ? Lang(lang, 'loading')
                : Lang(lang, 'reschedule')}
            </button>
            <button
              type='button'
              onClick={onConfirm}
              disabled={isSubmitting || !selectedSlot}
              className='w-full rounded-full bg-[#EA2427] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#D91F22] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto'
            >
              {isSubmitting
                ? Lang(lang, 'loading')
                : Lang(lang, 'confirm_schedule')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default InterviewScheduleCard
